import { ComponentProps, ReactNode, useState } from 'react'
import { rectSortingStrategy, SortableContext } from '@dnd-kit/sortable'
import { Info } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import {
  RaidPlanAssignmentEntity,
  RaidPlanAssignmentType,
} from 'app/frontend/types/custom/RaidPlan'
import AssignmentBoxConfigurator from './AssignmentBoxConfigurator'
import AssignmentDraggableEntityChip from './AssignmentDraggableEntityChip'
import AssignmentEntityChip from './AssignmentEntityChip'
import AssignmentEntityGrid from './AssignmentEntityGrid'
import AssignmentGroupDropTarget from './AssignmentGroupDropTarget'
import {
  FocusedAssignmentScope,
  getAssignedChipId,
  getAssignedEntityId,
  getGroupDroppableId,
} from './assignmentDnd'
import getAssignmentEntityIdentity from './assignmentEntityIdentity'
import {
  AssignmentViewMode,
  getAbilityReminderText,
  getAssignmentReminderText,
  getAssignmentTargetLabel,
  getCascadedReminderText,
  getChipReminderTimeShift,
  getEffectiveAssignmentType,
  getEffectiveExpectedEntities,
  getGroupCascadeReminders,
  getHeaderReminderTimeShift,
  isGeneralAssignmentType,
} from './assignmentTimeline'
import AssignmentExpectedEntitiesCount from './AssignmentExpectedEntitiesCount'
import {
  getAnyPlayerFillerCount,
  getPlannedDisplayedCount,
} from './assignmentEntityCounting'
import { buildAssignmentGroupColumns } from './assignmentGroupLayout'
import Card from 'app/frontend/base/components/Card'
import {
  AssignmentPhaseTimestampBadge,
  AssignmentPhaseTimestampBadgeData,
} from './AssignmentPhaseTimestampBadge'
import AssignmentDescriptionContent, {
  AssignmentDescriptionControl,
} from './AssignmentDescriptionField'
import AssignmentNoteSettingsDialog from './AssignmentNoteSettingsDialog'
import AssignmentScopeReminderText from './AssignmentScopeReminderText'
import MarkerText from './MarkerText'

const assignmentGroupGridStyle = {
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 12rem), 1fr))',
}

type Scope = {
  id: string
  label: string
  castIndexes: number[]
}

type NoteSettings = {
  message?: string
  ttsMessage?: string
  expectedEntities?: number
  rgb?: string
  cascadeReminders?: number
  offsetReminders?: number
  reminderDuration?: number
}

type Props = {
  scope: Scope
  spellId: number
  castTimestamps: AssignmentPhaseTimestampBadgeData[]
  entitiesByGroup: RaidPlanAssignmentEntity[][]
  assignmentGroups: number
  canEdit: boolean
  paletteEntities: ComponentProps<
    typeof AssignmentBoxConfigurator
  >['paletteEntities']
  assignmentUsageCountByIdentity: Record<string, number>
  onSave: (entitiesByGroup: RaidPlanAssignmentEntity[][]) => void
  assignLabel: string
  showViewModeTabs: boolean
  abilityViewMode: AssignmentViewMode
  duplicateIdentities: Set<string>
  getEntityLabel: (entity: RaidPlanAssignmentEntity) => ReactNode
  groupLabel: (count: number) => string
  onAssignmentDialogOpenChange: (open: boolean) => void
  abilityKey: string
  focusedScope: FocusedAssignmentScope | null
  onFocusScope: (scope: FocusedAssignmentScope | null) => void
  popupOpen: boolean
  onPopupOpenChange: (open: boolean) => void
  showSidebar: boolean
  description?: string
  onDescriptionSave: (html: string) => Promise<void>
  noteSettingsByGroup: NoteSettings[]
  noteSettingsPlaceholder: { message: string; ttsMessage: string }
  rgbPlaceholder?: string
  onNoteSettingsSave: (boxIndex: number, next: NoteSettings) => void
  onEditForAllReminders?: () => void
  abilityMessage?: string
  abilityLabel: string
  abilityExpectedEntities?: number
  abilityCascadeReminders?: number
  abilityOffsetReminders?: number
  abilityReminderDuration?: number
  assignmentType?: RaidPlanAssignmentType
  isCustom?: boolean
}

const TeamPlanAssignmentScopeCard = ({
  scope,
  spellId,
  castTimestamps,
  entitiesByGroup,
  assignmentGroups,
  canEdit,
  paletteEntities,
  assignmentUsageCountByIdentity,
  onSave,
  assignLabel,
  showViewModeTabs,
  abilityViewMode,
  duplicateIdentities,
  getEntityLabel,
  groupLabel,
  onAssignmentDialogOpenChange,
  abilityKey,
  focusedScope,
  onFocusScope,
  popupOpen,
  onPopupOpenChange,
  showSidebar,
  description,
  onDescriptionSave,
  noteSettingsByGroup,
  noteSettingsPlaceholder,
  rgbPlaceholder,
  onNoteSettingsSave,
  onEditForAllReminders,
  abilityMessage,
  abilityLabel,
  abilityExpectedEntities,
  abilityCascadeReminders,
  abilityOffsetReminders,
  abilityReminderDuration,
  assignmentType,
  isCustom = false,
}: Props) => {
  const { t } = useTranslation()
  const renderAnyPlayerFillerChips = (
    count: number,
    startIndex: number,
    boxIndex: number,
    firstEntityIndex: number,
  ) =>
    count > 0
      ? Array.from({ length: count }, (_, fillerIndex) => (
          <AssignmentEntityChip
            key={`any-player-filler-${startIndex}-${fillerIndex}`}
            index={startIndex + fillerIndex}
            {...getChipTimeShift(boxIndex, firstEntityIndex + fillerIndex)}
            label={t(
              'dashboard.team.plans.assignments.entities.by-role.anyone',
            )}
            className='w-full border-dashed bg-transparent hover:bg-transparent text-muted-foreground'
          />
        ))
      : null
  const [isEditingDescription, setIsEditingDescription] = useState(false)
  const showNoteSettings = abilityViewMode !== 'all_together'
  const isScopeFocused =
    focusedScope?.abilityKey === abilityKey &&
    focusedScope?.scopeId === scope.id
  const groupSlotCounts = entitiesByGroup.map((groupEntities, boxIndex) => {
    const expected = getEffectiveExpectedEntities(
      abilityExpectedEntities,
      noteSettingsByGroup[boxIndex]?.expectedEntities,
    )
    const fillerCount = getAnyPlayerFillerCount(groupEntities, expected)
    return groupEntities.length + fillerCount
  })
  const groupStartOffsets = groupSlotCounts.reduce<number[]>(
    (acc, _count, index) => {
      acc[index] = index === 0 ? 0 : acc[index - 1] + groupSlotCounts[index - 1]
      return acc
    },
    [],
  )
  const getChipTimeShift = (boxIndex: number, entityIndex: number) =>
    getChipReminderTimeShift({
      assignmentGroups,
      abilityCascade: abilityCascadeReminders,
      boxCascade: noteSettingsByGroup[boxIndex]?.cascadeReminders,
      entityIndex,
    })
  const getHeaderTimeShift = (boxIndex: number) =>
    getHeaderReminderTimeShift({
      assignmentGroups,
      abilityOffset: abilityOffsetReminders,
      boxOffset: noteSettingsByGroup[boxIndex]?.offsetReminders,
      abilityCascade: abilityCascadeReminders,
      groupIndex: boxIndex,
    })
  const groupCascade = getGroupCascadeReminders(
    assignmentGroups,
    abilityCascadeReminders,
  )
  const getGroupReminderPreview = (boxIndex: number, message?: string) => {
    const reminderText = getAssignmentReminderText({
      message,
      abilityMessage,
      abilityLabel,
      assignmentType,
      isCustom,
    })
    return groupCascade
      ? getCascadedReminderText(reminderText, boxIndex)
      : reminderText
  }
  const focusedGroupIndex =
    isScopeFocused && focusedScope ? focusedScope.groupIndex : 0
  const targetLabel = getAssignmentTargetLabel(abilityLabel, [
    showViewModeTabs && scope.label,
    assignmentGroups > 1 && groupLabel(focusedGroupIndex + 1),
  ])
  const clearGroupButton = (groupIndex: number) =>
    canEdit && isScopeFocused && focusedScope?.groupIndex === groupIndex ? (
      <Button
        type='button'
        variant='outline'
        size='xs'
        disabled={(entitiesByGroup[groupIndex]?.length ?? 0) === 0}
        onClick={() =>
          onSave(
            entitiesByGroup.map((group, index) =>
              index === groupIndex ? [] : group,
            ),
          )
        }
      >
        {t('dashboard.team.plans.assignments.clear')}
      </Button>
    ) : null
  const isGeneralAssignment = isGeneralAssignmentType(
    getEffectiveAssignmentType(assignmentType),
  )
  const noteLabel = getAbilityReminderText(
    abilityMessage,
    abilityLabel,
    assignmentType,
    isCustom,
  )
  const scopeTitle = isGeneralAssignment ? noteLabel : scope.label
  const showScopeTitle =
    Boolean(scopeTitle) &&
    !(
      isGeneralAssignment &&
      noteLabel.trim().toLowerCase() === abilityLabel.trim().toLowerCase()
    )
  const scopeReminderText =
    !isGeneralAssignment && assignmentGroups === 1
      ? getAssignmentReminderText({
          message: noteSettingsByGroup[0]?.message,
          abilityMessage,
          abilityLabel,
          assignmentType,
          isCustom,
        })
      : undefined
  const primaryExpected = getEffectiveExpectedEntities(
    abilityExpectedEntities,
    noteSettingsByGroup[0]?.expectedEntities,
  )
  const primaryDisplayedCount = getPlannedDisplayedCount(
    entitiesByGroup[0] ?? [],
    primaryExpected,
  )
  const groupColumns = buildAssignmentGroupColumns(
    entitiesByGroup.map((_, boxIndex) =>
      getEffectiveExpectedEntities(
        abilityExpectedEntities,
        noteSettingsByGroup[boxIndex]?.expectedEntities,
      ),
    ),
  )
  const renderGroup = (boxIndex: number) => {
    const groupEntities = entitiesByGroup[boxIndex] ?? []
    const expected = getEffectiveExpectedEntities(
      abilityExpectedEntities,
      noteSettingsByGroup[boxIndex]?.expectedEntities,
    )
    const displayedCount = getPlannedDisplayedCount(groupEntities, expected)
    const fillerCount = getAnyPlayerFillerCount(groupEntities, expected)
    return (
      <div key={boxIndex} className='flex min-h-0 min-w-0 flex-col'>
        {assignmentGroups > 1 ? (
          <div className='mb-1 pb-1 flex items-center gap-2 flex-wrap'>
            <div className={isGeneralAssignment ? 'text-sm' : 'text-xs'}>
              {isGeneralAssignment ? (
                <MarkerText
                  text={
                    noteSettingsByGroup[boxIndex]?.message?.trim() ||
                    groupLabel(boxIndex + 1)
                  }
                />
              ) : (
                groupLabel(boxIndex + 1)
              )}
            </div>
            <AssignmentExpectedEntitiesCount
              actual={displayedCount.actual}
              expected={expected}
              disabled={displayedCount.disabling}
            />
            {canEdit ? (
              <AssignmentNoteSettingsDialog
                label={
                  isGeneralAssignment
                    ? noteSettingsByGroup[boxIndex]?.message?.trim() ||
                      groupLabel(boxIndex + 1)
                    : `${scope.label} (${groupLabel(boxIndex + 1)})`
                }
                message={noteSettingsByGroup[boxIndex]?.message}
                ttsMessage={noteSettingsByGroup[boxIndex]?.ttsMessage}
                expectedEntities={
                  noteSettingsByGroup[boxIndex]?.expectedEntities
                }
                rgb={noteSettingsByGroup[boxIndex]?.rgb}
                cascadeReminders={
                  noteSettingsByGroup[boxIndex]?.cascadeReminders
                }
                offsetReminders={noteSettingsByGroup[boxIndex]?.offsetReminders}
                reminderDuration={
                  noteSettingsByGroup[boxIndex]?.reminderDuration
                }
                abilityCascadeReminders={
                  assignmentGroups === 1 ? abilityCascadeReminders : undefined
                }
                abilityOffsetReminders={abilityOffsetReminders}
                abilityReminderDuration={abilityReminderDuration}
                messagePlaceholder={noteSettingsPlaceholder.message}
                ttsPlaceholder={noteSettingsPlaceholder.ttsMessage}
                expectedEntitiesPlaceholder={abilityExpectedEntities}
                rgbPlaceholder={rgbPlaceholder}
                canEdit={canEdit}
                useNoteLabel={isGeneralAssignment}
                onSave={(next) => onNoteSettingsSave(boxIndex, next)}
                onEditForAllReminders={onEditForAllReminders}
              />
            ) : null}
            {isGeneralAssignment ? (
              <span className='ml-auto'>{clearGroupButton(boxIndex)}</span>
            ) : (
              <div className='flex min-w-[7ch] flex-1 items-center gap-2'>
                <AssignmentScopeReminderText
                  className='ml-0 min-w-[7ch] shrink'
                  text={getGroupReminderPreview(
                    boxIndex,
                    noteSettingsByGroup[boxIndex]?.message,
                  )}
                  color={noteSettingsByGroup[boxIndex]?.rgb || rgbPlaceholder}
                  {...getHeaderTimeShift(boxIndex)}
                />
                <span className='ml-auto shrink-0'>
                  {clearGroupButton(boxIndex)}
                </span>
              </div>
            )}
          </div>
        ) : null}
        {canEdit ? (
          <AssignmentGroupDropTarget
            id={getGroupDroppableId(abilityKey, scope.id, boxIndex)}
            className={cn(
              'border-dashed p-2 min-h-12 grow h-full cursor-pointer',
              isScopeFocused && focusedScope?.groupIndex === boxIndex
                ? 'border-primary'
                : 'hover:border-primary/50',
            )}
            onClick={() => {
              if (!showSidebar) return
              if (isScopeFocused && focusedScope?.groupIndex === boxIndex) {
                onFocusScope(null)
                return
              }
              onFocusScope({
                abilityKey,
                scopeId: scope.id,
                groupIndex: boxIndex,
              })
            }}
          >
            {groupEntities.length === 0 ? (
              fillerCount > 0 ? (
                <AssignmentEntityGrid>
                  {renderAnyPlayerFillerChips(
                    fillerCount,
                    groupStartOffsets[boxIndex],
                    boxIndex,
                    0,
                  )}
                </AssignmentEntityGrid>
              ) : (
                <div className='text-sm text-muted-foreground'>
                  {t(
                    isScopeFocused && focusedScope?.groupIndex === boxIndex
                      ? 'dashboard.team.plans.assignments.drop-or-click-focused-hint'
                      : 'dashboard.team.plans.assignments.drop-or-click-hint',
                  )}
                </div>
              )
            ) : (
              <SortableContext
                items={groupEntities.map((entity, entityIndex) =>
                  getAssignedChipId(
                    abilityKey,
                    scope.id,
                    boxIndex,
                    getAssignedEntityId(entity, entityIndex),
                  ),
                )}
                strategy={rectSortingStrategy}
              >
                <AssignmentEntityGrid>
                  {groupEntities.map((entity, entityIndex) => {
                    const entityIdentity = getAssignmentEntityIdentity(entity)
                    const showDuplicateInfo =
                      entity.kind !== 'by_role' &&
                      entity.kind !== 'by_class' &&
                      abilityViewMode !== 'all_separate' &&
                      duplicateIdentities.has(entityIdentity)
                    const entityId = getAssignedEntityId(entity, entityIndex)
                    return (
                      <AssignmentDraggableEntityChip
                        key={entityId}
                        sortableId={getAssignedChipId(
                          abilityKey,
                          scope.id,
                          boxIndex,
                          entityId,
                        )}
                        index={groupStartOffsets[boxIndex] + entityIndex}
                        {...getChipTimeShift(boxIndex, entityIndex)}
                        label={getEntityLabel(entity)}
                        canEdit={canEdit}
                        info={showDuplicateInfo}
                        warningSlot={
                          showDuplicateInfo ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className='inline-flex items-center text-info-foreground'>
                                  <Info className='h-3.5 w-3.5' />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                This entity is in multiple groups
                              </TooltipContent>
                            </Tooltip>
                          ) : undefined
                        }
                        onClick={() => {
                          if (!showSidebar) return
                          onFocusScope({
                            abilityKey,
                            scopeId: scope.id,
                            groupIndex: boxIndex,
                          })
                        }}
                        onRemove={() => {
                          onFocusScope({
                            abilityKey,
                            scopeId: scope.id,
                            groupIndex: boxIndex,
                          })
                          onSave(
                            entitiesByGroup.map((group, groupIndex) =>
                              groupIndex === boxIndex
                                ? group.filter(
                                    (_, index) => index !== entityIndex,
                                  )
                                : group,
                            ),
                          )
                        }}
                      />
                    )
                  })}
                  {renderAnyPlayerFillerChips(
                    fillerCount,
                    groupStartOffsets[boxIndex] + groupEntities.length,
                    boxIndex,
                    groupEntities.length,
                  )}
                </AssignmentEntityGrid>
              </SortableContext>
            )}
          </AssignmentGroupDropTarget>
        ) : groupEntities.length === 0 ? (
          fillerCount > 0 ? (
            <AssignmentEntityGrid>
              {renderAnyPlayerFillerChips(
                fillerCount,
                groupStartOffsets[boxIndex],
                boxIndex,
                0,
              )}
            </AssignmentEntityGrid>
          ) : (
            <div className='text-xs text-muted-foreground text-italic'>
              No entities assigned yet
            </div>
          )
        ) : (
          <AssignmentEntityGrid>
            {groupEntities.map((entity, entityIndex) => {
              const entityIdentity = getAssignmentEntityIdentity(entity)
              const showDuplicateInfo =
                entity.kind !== 'by_role' &&
                entity.kind !== 'by_class' &&
                abilityViewMode !== 'all_separate' &&
                duplicateIdentities.has(entityIdentity)
              return (
                <AssignmentDraggableEntityChip
                  key={getAssignedEntityId(entity, entityIndex)}
                  sortableId={getAssignedChipId(
                    abilityKey,
                    scope.id,
                    boxIndex,
                    getAssignedEntityId(entity, entityIndex),
                  )}
                  index={groupStartOffsets[boxIndex] + entityIndex}
                  {...getChipTimeShift(boxIndex, entityIndex)}
                  label={getEntityLabel(entity)}
                  canEdit={false}
                  info={showDuplicateInfo}
                  warningSlot={
                    showDuplicateInfo ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className='inline-flex items-center text-info-foreground'>
                            <Info className='h-3.5 w-3.5' />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          This entity is in multiple groups
                        </TooltipContent>
                      </Tooltip>
                    ) : undefined
                  }
                />
              )
            })}
            {renderAnyPlayerFillerChips(
              fillerCount,
              groupStartOffsets[boxIndex] + groupEntities.length,
              boxIndex,
              groupEntities.length,
            )}
          </AssignmentEntityGrid>
        )}
      </div>
    )
  }

  return (
    <Card>
      <div className='mb-2 flex flex-wrap items-center gap-4'>
        <div className='flex items-center gap-2'>
          {showScopeTitle ? (
            <div className='font-medium text-sm'>
              <MarkerText text={scopeTitle} />
            </div>
          ) : null}
          {assignmentGroups === 1 ? (
            <AssignmentExpectedEntitiesCount
              actual={primaryDisplayedCount.actual}
              expected={primaryExpected}
              disabled={primaryDisplayedCount.disabling}
            />
          ) : null}
        </div>
        <div className='flex min-w-0 flex-1 flex-wrap items-center gap-1 text-xs text-muted-foreground'>
          {castTimestamps.map((castTimestamp, index) => (
            <AssignmentPhaseTimestampBadge
              key={`${scope.id}:${index}`}
              phaseLabel={castTimestamp.phaseLabel}
              timeLabel={castTimestamp.timeLabel}
            />
          ))}
          {scopeReminderText ? (
            <div className='ml-2 flex min-w-[7ch] flex-1 items-center gap-2'>
              <AssignmentScopeReminderText
                className='ml-0 min-w-[7ch] shrink'
                text={scopeReminderText}
                color={noteSettingsByGroup[0]?.rgb || rgbPlaceholder}
                {...getHeaderTimeShift(0)}
              />
              {assignmentGroups === 1 ? (
                <span className='ml-auto shrink-0'>{clearGroupButton(0)}</span>
              ) : null}
            </div>
          ) : assignmentGroups === 1 ? (
            <span className='ml-auto shrink-0'>{clearGroupButton(0)}</span>
          ) : null}
        </div>
        <div className='flex items-center gap-2'>
          <AssignmentDescriptionControl
            description={description}
            canEdit={canEdit}
            isEditing={isEditingDescription}
            onStartEdit={() => setIsEditingDescription(true)}
          />
          {canEdit ? (
            <>
              <Button
                type='button'
                variant='outline'
                size='custom'
                className='h-7 py-0.5 px-2'
                onClick={() => {
                  if (showSidebar && !isScopeFocused) {
                    onFocusScope({
                      abilityKey,
                      scopeId: scope.id,
                      groupIndex: 0,
                    })
                    return
                  }
                  onPopupOpenChange(true)
                }}
              >
                {assignLabel}
              </Button>
              <AssignmentBoxConfigurator
                spellId={spellId}
                targetLabel={targetLabel}
                entitiesByGroup={entitiesByGroup}
                expectedEntitiesByGroup={entitiesByGroup.map((_, groupIndex) =>
                  getEffectiveExpectedEntities(
                    abilityExpectedEntities,
                    noteSettingsByGroup[groupIndex]?.expectedEntities,
                  ),
                )}
                paletteEntities={paletteEntities}
                assignmentUsageCountByIdentity={assignmentUsageCountByIdentity}
                canEdit={canEdit}
                onSave={onSave}
                open={popupOpen}
                onOpenChange={(nextOpen) => {
                  onPopupOpenChange(nextOpen)
                  onAssignmentDialogOpenChange(nextOpen)
                }}
              />
            </>
          ) : null}
          {canEdit && showNoteSettings && assignmentGroups === 1 ? (
            <AssignmentNoteSettingsDialog
              label={scope.label}
              message={noteSettingsByGroup[0]?.message}
              ttsMessage={noteSettingsByGroup[0]?.ttsMessage}
              expectedEntities={noteSettingsByGroup[0]?.expectedEntities}
              rgb={noteSettingsByGroup[0]?.rgb}
              cascadeReminders={noteSettingsByGroup[0]?.cascadeReminders}
              offsetReminders={noteSettingsByGroup[0]?.offsetReminders}
              reminderDuration={noteSettingsByGroup[0]?.reminderDuration}
              abilityCascadeReminders={abilityCascadeReminders}
              abilityOffsetReminders={abilityOffsetReminders}
              abilityReminderDuration={abilityReminderDuration}
              messagePlaceholder={noteSettingsPlaceholder.message}
              ttsPlaceholder={noteSettingsPlaceholder.ttsMessage}
              expectedEntitiesPlaceholder={abilityExpectedEntities}
              rgbPlaceholder={rgbPlaceholder}
              canEdit={canEdit}
              onSave={(next) => onNoteSettingsSave(0, next)}
              onEditForAllReminders={onEditForAllReminders}
            />
          ) : null}
        </div>
      </div>

      <AssignmentDescriptionContent
        description={description}
        isEditing={isEditingDescription}
        clearTarget='this cast'
        onStopEdit={() => setIsEditingDescription(false)}
        onSave={async (html) => {
          await onDescriptionSave(html)
          setIsEditingDescription(false)
        }}
        className='mb-2'
      />

      <div
        className='grid min-w-0 gap-2 items-stretch'
        style={assignmentGroupGridStyle}
      >
        {groupColumns.map((column) =>
          column.length > 1 ? (
            <div
              key={`stack-${column[0]}`}
              className='flex min-h-0 min-w-0 flex-col gap-2'
            >
              {column.map((boxIndex) => renderGroup(boxIndex))}
            </div>
          ) : (
            renderGroup(column[0])
          ),
        )}
      </div>
    </Card>
  )
}

export default TeamPlanAssignmentScopeCard
